const API_BASE =
  process.env.REACT_APP_EMAIL_LISTS_API_URL || 'https://kzni23qc9h.execute-api.eu-central-1.amazonaws.com';

const WHITELIST_CHECK_API_BASE = process.env.REACT_APP_SAVED_FILTERS_API_URL || '';

export type EmailLists = { whitelist: string[]; blacklist: string[] };

/** Fallback */
export const DEFAULT_EMAIL_BLACKLIST = [
  '126',
  '139',
  '153fdd',
  '1623',
  '163',
  '189',
  'aol',
  'asaud',
  'ceeun',
  'cross.edu.pl',
  'e-mail.edu.pl',
  'fast.edu.pl',
  'foxmail',
  'gmail',
  'gmx',
  'googlemail',
  'guerrillamailblock.com',
  'hotmail',
  'icloud',
  'live',
  'me',
  'moakt.cc',
  'msn',
  'naver',
  'omail.edu.pl',
  'outlook',
  'promail.edu.pl',
  'protonmail',
  'qq',
  'sina',
  'smallntm.lol',
  'tthy.site',
  'vip',
  'yahoo',
] as const;

let cached: EmailLists | null = null;
const whitelistExceptionCache = new Map<string, boolean>();

function normalizeListItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items.map(item => (typeof item === 'string' ? item.trim().toLowerCase() : '')).filter(Boolean);
}

export function mergeWithDefaultBlacklist(blacklist: string[] = []): string[] {
  return [...new Set([...DEFAULT_EMAIL_BLACKLIST, ...blacklist])];
}

function buildLists(blacklist: string[]): EmailLists {
  return {
    whitelist: [],
    blacklist: mergeWithDefaultBlacklist(blacklist),
  };
}

export function getDefaultEmailLists(): EmailLists {
  return buildLists([]);
}

export async function getEmailLists(forceRefresh = false): Promise<EmailLists> {
  if (cached && !forceRefresh) return cached;

  try {
    const blacklistResponse = await fetch(`${API_BASE}/lists/blacklist`);

    if (!blacklistResponse.ok) {
      throw new Error('Email lists request failed');
    }

    const blacklistData = await blacklistResponse.json();
    cached = buildLists(normalizeListItems(blacklistData.items));
    return cached;
  } catch (error) {
    console.warn('Email lists fetch failed, using default blacklist fallback', error);
    if (cached) return cached;
    cached = getDefaultEmailLists();
    return cached;
  }
}

export function getCachedEmailLists() {
  return cached ?? getDefaultEmailLists();
}

export async function checkRemoteEmailWhitelist(email: string): Promise<boolean> {
  const cleaned = email?.trim()?.toLowerCase();
  if (!cleaned || !WHITELIST_CHECK_API_BASE) return false;
  if (whitelistExceptionCache.has(cleaned)) {
    return whitelistExceptionCache.get(cleaned) === true;
  }

  try {
    const response = await fetch(`${WHITELIST_CHECK_API_BASE}/email/whitelisted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: cleaned }),
    });

    if (!response.ok) return false;

    const data = await response.json();
    const allowed = data?.allowed === true;
    whitelistExceptionCache.set(cleaned, allowed);
    return allowed;
  } catch (error) {
    console.warn('Email whitelist check failed', error);
    return false;
  }
}
